// tools-add-remove-runner.ts
//
// Thực thi bulk add/remove tool: discover → chọn (checkbox hoặc chọn-hết) →
// loop enable/disableToolByName. Dùng bởi `avatar tools add`/`remove` VÀ bởi
// cú pháp `avatar tools enable all`/`disable all` (delegate all:true — DRY,
// không thêm call-site mới tới hub CRITICAL enableToolByName/disableToolByName).
//
// Tách khỏi tools.ts để giữ file đăng ký lệnh <200 dòng + runner test được
// mà không cần đăng ký commander.

import { resolve } from "node:path";
import { checkbox, confirm } from "@inquirer/prompts";
import { discoverEnabledAndAvailableTools } from "../lib/discover-pack-tools-and-defaults.js";
import { log } from "../lib/terminal-logger.js";
import {
  buildAddChoices,
  buildRemoveChoices,
  resolveNonInteractiveSelection,
} from "../lib/tool-add-remove-selection.js";
import { disableToolByName, enableToolByName } from "../lib/tool-enable-disable-orchestrator.js";

export interface ToolOptions {
  target?: string;
}

export interface AddRemoveOptions extends ToolOptions {
  all?: boolean;
  yes?: boolean;
  /** Danh sách tên tool, phân tách bằng dấu phẩy — đường tường minh thay cho prompt. */
  tools?: string;
}

/**
 * Tách `--tools a,b,c` thành danh sách tên, bỏ khoảng trắng và phần tử rỗng.
 * Hàm thuần để test được mà không cần dựng commander.
 */
export function parseToolsFlag(raw: string | undefined): string[] {
  if (!raw) return [];
  return raw
    .split(",")
    .map((name) => name.trim())
    .filter((name) => name.length > 0);
}

export function resolveWorkspace(opts: ToolOptions): string {
  return resolve(opts.target ?? process.cwd());
}

// Quyết định selection:
//   --tools a,b  → đúng danh sách đó (giao với candidate)
//   --all        → hết candidate
//   không TTY, không cờ nào → CHỌN 0 TOOL + cảnh báo stderr (no-op an toàn)
//   còn lại      → prompt checkbox
// Trả về danh sách tên tool đã chọn (có thể rỗng).
//
// ⚠️ BREAKING so với bản trước: `!process.stdout.isTTY` một mình từng đi thẳng vào
// `resolveNonInteractiveSelection` = **chọn HẾT**. Nghĩa là `avatar tools remove`
// chạy từ CI, script, hay extension (stdin đóng) sẽ **gỡ sạch tool, không hỏi ai** —
// hố mở dù người gọi không truyền cờ nào, nên thêm cờ mới không bịt được. Mặc định
// im lặng cho một thao tác ghi `settings.json` là thứ phải sửa ở chính điều kiện,
// không phải ở lớp trên.
//
// Vẫn là **skip mềm** (exit 0), không phải thoát cứng — đúng D1: `isTTY` không bao
// giờ gây thoát cứng; ở đây nó chỉ đổi mặc định theo hướng an toàn hơn (D6).
async function selectTools(
  candidates: string[],
  preChecked: string[],
  opts: AddRemoveOptions,
  verb: "add" | "remove",
): Promise<string[]> {
  const explicit = parseToolsFlag(opts.tools);
  if (explicit.length > 0) {
    return explicit.filter((name) => candidates.includes(name));
  }
  if (opts.all === true) {
    return resolveNonInteractiveSelection(candidates);
  }
  if (!process.stdout.isTTY) {
    // stderr, không stdout: đường stdout có thể đang được máy khác đọc.
    process.stderr.write(
      `Không có TTY và không có --tools/--all ⇒ không ${verb === "add" ? "cài" : "gỡ"} tool nào. ` +
        `Nêu rõ bằng --tools <a,b,c> hoặc --all.\n`,
    );
    return [];
  }
  const choices =
    verb === "add" ? buildAddChoices(candidates, preChecked) : buildRemoveChoices(candidates);
  return await checkbox<string>({
    message:
      verb === "add"
        ? "Chọn tool để cài (space chọn, enter xác nhận):"
        : "Chọn tool để gỡ (space chọn, enter xác nhận):",
    choices,
  });
}

// `avatar tools add` (và `enable all` khi opts.all=true) — list available,
// chọn 1-n, enable từng cái. Bulk best-effort: 1 tool bị skip KHÔNG chặn các tool
// còn lại — luôn kết thúc exit 0.
// enableToolByName tự log lý do cụ thể khi trả false (thiếu manifest, non-TTY,
// user hủy, fetch fail, 1-model...) — không cần hint chung ở đây (từng bị lệch
// hướng/double-log khi mọi lý do false đều đã có message riêng).
export async function runAdd(opts: AddRemoveOptions): Promise<void> {
  const ws = resolveWorkspace(opts);
  const { available, enabled } = await discoverEnabledAndAvailableTools(ws);
  if (available.length === 0) {
    log.dim("Không có tool available (pack chưa có tools/, hoặc chưa sync).");
    return;
  }
  const selected = await selectTools(available, enabled, opts, "add");
  if (selected.length === 0) {
    log.dim("Không chọn tool nào — hủy, không thay đổi.");
    return;
  }
  for (const name of selected) {
    await enableToolByName(ws, name);
  }
}

// `avatar tools remove` (và `disable all` khi opts.all=true) — list enabled,
// chọn 1-n, disable từng cái. all=true → KHÔNG confirm (đồng nhất `remove --all`
// và `disable <name>` đơn — gõ literal 'all' đã là opt-in bulk tường minh).
export async function runRemove(opts: AddRemoveOptions): Promise<void> {
  const ws = resolveWorkspace(opts);
  const { enabled } = await discoverEnabledAndAvailableTools(ws);
  if (enabled.length === 0) {
    log.dim("Không có tool nào đang bật — không có gì để gỡ.");
    return;
  }
  const selected = await selectTools(enabled, [], opts, "remove");
  if (selected.length === 0) {
    log.dim("Không chọn tool nào — hủy, không thay đổi.");
    return;
  }
  // remove sửa settings.json → xác nhận trước (trừ khi --yes, --all, --tools, hoặc
  // non-TTY). `--tools` là opt-in tường minh: người gọi đã kê đích danh tool nào,
  // hỏi lại lần nữa trong ngữ cảnh không ai bấm được chỉ làm lệnh treo.
  const needConfirm =
    opts.yes !== true &&
    opts.all !== true &&
    parseToolsFlag(opts.tools).length === 0 &&
    process.stdout.isTTY;
  if (needConfirm) {
    const ok = await confirm({
      message: `Gỡ ${selected.length} tool: ${selected.join(", ")}?`,
      default: true,
    });
    if (!ok) {
      log.dim("Hủy — không gỡ tool nào.");
      return;
    }
  }
  for (const name of selected) {
    await disableToolByName(ws, name);
  }
}
